/**
 * Runtime configuration injected by the Razor layout
 * (Controls/Mvc/SupportChatWidget.cs) before the widget script runs.
 *
 * Nothing here is secret and nothing here is trusted by the backend — the
 * event id is only a convenience so the customer is not asked "which event?"
 * when they open the widget on an event page. The server re-validates every
 * value it receives.
 */
export interface SupportChatConfig {
	language: string;
	eventId: number | null;
	eventName: string | null;
	/** Public support address shown in the "talk to a human" step. */
	supportEmail: string;
	/** Public support phone (InstanceConfig.Company.PhoneNumber), null when not configured. */
	supportPhone: string | null;
	/** Relative API root, e.g. `/api/v1` (one controller per endpoint below it). Never an absolute host. */
	apiBase: string;
	/** Public review page (Google) offered after a successfully resolved flow; null hides the review card. */
	reviewUrl: string | null;
	/** Privacy policy page linked from the widget footer (GDPR art. 13 information); null hides the link. */
	privacyUrl: string | null;
}

/** Only absolute https links (review) or same-site paths (privacy) may become an href - never javascript:/data:. */
function safeLink(value: unknown, allowRelative: boolean): string | null {
	if (typeof value !== 'string' || value.length === 0 || value.length > 2000) {
		return null;
	}
	if (/^https:\/\//i.test(value)) {
		return value;
	}
	if (allowRelative && /^\/(?!\/)/.test(value)) {
		return value;
	}
	return null;
}

declare global {
	interface Window {
		__invSupportChat?: Partial<SupportChatConfig>;
	}
}

export function readConfig(): SupportChatConfig {
	const w = typeof window !== 'undefined' ? window : ({} as Window);
	const injected = w.__invSupportChat ?? {};
	const docLang = typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null;
	return {
		language: injected.language ?? docLang ?? 'sk',
		eventId: injected.eventId ?? null,
		eventName: injected.eventName ?? null,
		supportEmail: injected.supportEmail ?? 'info@inviton.eu',
		supportPhone: injected.supportPhone ?? null,
		apiBase: injected.apiBase ?? '/api/v1',
		reviewUrl: safeLink(injected.reviewUrl, false),
		privacyUrl: safeLink(injected.privacyUrl, true),
	};
}

/** Localhost / Azure dev slots only — mock backend must never be reachable on production hosts. */
export function isDevHost(hostname = typeof location !== 'undefined' ? location.hostname : ''): boolean {
	const h = hostname.toLowerCase();
	return h === 'localhost' || h === '127.0.0.1' || h.includes('invitondev') || h.includes('inviton1dev') || h.includes('inviton1-release-slot');
}
